import { useEffect, useRef, useState } from 'react'
import { IconoBuscar } from '../../componentes/iconos'
import { ESTADOS, type FiltrosAvisos } from '../../hooks/useFiltrosAvisos'
import { etiquetaEstadoPlural } from './formato'
import { SEMANAS_ATENCION, SEMANAS_CRITICO } from './semanas'

/** Sin espera, cada tecla seria una peticion y una entrada de historial. */
const ESPERA_BUSQUEDA = 300

/**
 * Los dos primeros son los mismos umbrales con los que se destacan las filas:
 * el operador filtra por lo que la tabla ya le esta senalando, en vez de tener
 * que traducir entre dos escalas distintas.
 */
const CORTES_SEMANAS = [SEMANAS_ATENCION, SEMANAS_CRITICO, 12] as const

interface Props {
  filtros: FiltrosAvisos
  onCambiar: (clave: 'estado' | 'semanas_minimas' | 'q', valor: string | undefined) => void
}

const CLASE_CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo'

export function BarraFiltrosAvisos({ filtros, onCambiar }: Props) {
  const [texto, setTexto] = useState(filtros.q)
  // Ultimo valor que este componente ha puesto en la url. Sirve para distinguir
  // "lo he escrito yo" de "la url ha cambiado por fuera" (atras del navegador).
  const ultimoEnviado = useRef(filtros.q)

  useEffect(() => {
    if (filtros.q === ultimoEnviado.current) return
    ultimoEnviado.current = filtros.q
    setTexto(filtros.q)
  }, [filtros.q])

  useEffect(() => {
    if (texto === ultimoEnviado.current) return
    const temporizador = setTimeout(() => {
      ultimoEnviado.current = texto
      onCambiar('q', texto || undefined)
    }, ESPERA_BUSQUEDA)
    return () => clearTimeout(temporizador)
  }, [texto, onCambiar])

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <label htmlFor="buscador-avisos" className="block text-sm font-medium text-tinta-2">
          Buscar cliente
        </label>
        <div className="relative mt-1">
          <IconoBuscar
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
          />
          <input
            id="buscador-avisos"
            type="search"
            autoComplete="off"
            placeholder="Nombre o teléfono"
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            className={`${CLASE_CAMPO} pl-9`}
          />
        </div>
      </div>

      <div>
        <label htmlFor="estado-aviso" className="block text-sm font-medium text-tinta-2">
          Estado
        </label>
        <select
          id="estado-aviso"
          value={filtros.estado ?? ''}
          onChange={(evento) => onCambiar('estado', evento.target.value || undefined)}
          className={`${CLASE_CAMPO} mt-1`}
        >
          {/* Las opciones salen del enum del documento OpenAPI: una lista a mano
              acabaria ofreciendo un valor que el endpoint rechaza con un 422. */}
          <option value="">Todos los estados</option>
          {ESTADOS.map((estado) => (
            <option key={estado} value={estado}>
              {etiquetaEstadoPlural(estado)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="semanas-minimas" className="block text-sm font-medium text-tinta-2">
          Semanas sin pedir
        </label>
        <select
          id="semanas-minimas"
          value={filtros.semanas_minimas === undefined ? '' : String(filtros.semanas_minimas)}
          onChange={(evento) => onCambiar('semanas_minimas', evento.target.value || undefined)}
          className={`${CLASE_CAMPO} mt-1`}
        >
          <option value="">Todas</option>
          {CORTES_SEMANAS.map((corte) => (
            <option key={corte} value={corte}>
              {corte} o más
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
